import { isEmpty, isNil, lensPath, set } from 'ramda';
import { type ReactNode, createContext, useContext, useReducer } from 'react';
import translations from '../../../../../translations';
import type { Confirmation } from '../../../../../types/generic';
import type { PageCondition, PageOperation, PageRule } from '../../../../../types/page';
import type { ValueElementIdReferenceAPI } from '../../../../../types/value';
import { usePageEditor } from '../../PageEditorProvider';
import {
    type PageConditionsAction,
    type PageConditionsState,
    pageConditionReducer,
} from './PageConditionsReducer';

interface PageConditionsProviderContext {
    state: PageConditionsState;
    updateCondition: (payload: PageCondition) => void;
    updateConditionIndex: (payload: number | null) => void;
    updateRuleIndex: (payload: number | null) => void;
    updateOperationIndex: (payload: number | null) => void;
    updateComparison: (payload: string) => void;
    updateRules: (payload: PageRule[]) => void;
    updateOperations: (payload: PageOperation[]) => void;
    reorderConditions: (payload: PageCondition[]) => void;
    reorderRules: (payload: PageRule[]) => void;
    reorderOperations: (payload: PageOperation[]) => void;
    deleteRule: (index: number) => void;
    deleteOperation: (index: number) => void;
    deleteCondition: (index: number) => void;
    toggleConditionIsActive: (index: number) => void;
    applyRule: (rule: PageRule) => void;
    applyOperation: (operation: PageOperation) => void;
    applyCondition: () => void;
    updateValues: (payload: ValueElementIdReferenceAPI[] | null) => void;
    updateRefreshValues: (payload: boolean) => void;
}

const Context = createContext<PageConditionsProviderContext | undefined>(undefined);

interface PageConditionsProviderProps {
    initialState?: PageConditionsState | null | undefined;
    children: ReactNode;
}

const defaultState = {
    conditionIndex: null,
    ruleIndex: null,
    operationIndex: null,
    condition: null,
    confirmation: null,
    values: null,
    refreshValues: true,
};

const PageConditionProvider = ({ initialState, children }: PageConditionsProviderProps) => {
    const {
        state: pageBuilderState,
        setPageConditions,
        toggleUnsavedChanges,
        setModifiedPageConditions,
        container,
    } = usePageEditor();
    const { pageConditions } = pageBuilderState.page;

    // Helps with testing as it gives the option to pass in some customized initial state.
    const combinedInitialState =
        isNil(initialState) || isEmpty(initialState)
            ? defaultState
            : {
                  ...defaultState,
                  ...initialState,
              };
    const [state, dispatch] = useReducer(pageConditionReducer, combinedInitialState);

    const update = (action: PageConditionsAction) => dispatch(action);

    // Custom actions for handling the state stored in the context.
    const updateConfirmation = (payload: Confirmation | null) =>
        update({ type: 'update_confirmation', payload });

    const updateRefreshValues = (payload: boolean) => update({ type: 'refresh_values', payload });
    const updateValues = (payload: ValueElementIdReferenceAPI[] | null) =>
        update({ type: 'update_values', payload });

    const updateCondition = (payload: PageCondition) =>
        update({ type: 'update_condition', payload });

    const updateConditionIndex = (payload: number | null) =>
        update({ type: 'update_condition_index', payload });

    const updateRuleIndex = (payload: number | null) =>
        update({ type: 'update_rule_index', payload });

    const updateOperationIndex = (payload: number | null) =>
        update({ type: 'update_operation_index', payload });

    const updateComparison = (payload: string) => update({ type: 'update_comparison', payload });

    const updateRules = (payload: PageRule[]) => update({ type: 'update_rules', payload });

    const updateOperations = (payload: PageOperation[]) =>
        update({ type: 'update_operations', payload });

    // Conditions are reordered straight from the conditions list, so the change is applied to the
    // page rather than to the condition currently open in the editor.
    const reorderConditions = (payload: PageCondition[]) => {
        setPageConditions(payload);

        // This is a page metadata change, so the page has to be saved now.
        toggleUnsavedChanges(true);
    };

    const reorderRules = (payload: PageRule[]) => {
        updateRules(payload);
        setModifiedPageConditions(true);
    };

    const reorderOperations = (payload: PageOperation[]) => {
        updateOperations(payload);
        setModifiedPageConditions(true);
    };

    const deleteRule = (index: number) => {
        updateConfirmation({
            show: true,
            title: translations.PAGE_BUILDER_delete_page_condition_rule_title,
            messages: [translations.PAGE_BUILDER_delete_page_condition_rule_message],
            buttonStyle: 'danger',
            buttonCaption: 'Delete',
            onCancel: () => updateConfirmation(null),
            onConfirm: () => {
                const { condition } = state;
                const conditionRuleRemoved = condition?.pageRules?.filter(
                    (_, i) => i !== index,
                ) as PageRule[];
                updateRules(conditionRuleRemoved);
                updateRuleIndex(null);
                updateConfirmation(null);
                setModifiedPageConditions(true);
            },
            container,
        });
    };

    const deleteOperation = (index: number) => {
        updateConfirmation({
            show: true,
            title: translations.PAGE_BUILDER_delete_page_condition_operation_title,
            messages: [translations.PAGE_BUILDER_delete_page_condition_operation_message],
            buttonStyle: 'danger',
            buttonCaption: 'Delete',
            onCancel: () => updateConfirmation(null),
            onConfirm: () => {
                const { condition } = state;
                const conditionOperationRemoved = condition?.pageOperations?.filter(
                    (_, i) => i !== index,
                ) as PageOperation[];
                updateOperations(conditionOperationRemoved);
                updateOperationIndex(null);
                updateConfirmation(null);
                setModifiedPageConditions(true);
            },
            container,
        });
    };

    const deleteCondition = (index: number) => {
        updateConfirmation({
            show: true,
            title: translations.PAGE_BUILDER_delete_page_condition_title,
            messages: [translations.PAGE_BUILDER_delete_page_condition_message],
            buttonStyle: 'danger',
            buttonCaption: 'Delete',
            onCancel: () => updateConfirmation(null),
            onConfirm: () => {
                const pageConditionRemoved = pageConditions?.filter(
                    (_, i) => i !== index,
                ) as PageCondition[];
                setPageConditions(pageConditionRemoved);
                updateConditionIndex(null);
                updateConfirmation(null);
                // Once the condition is deleted, this becomes a page metadata change,
                // so the page has to be saved now.
                setModifiedPageConditions(false);
                toggleUnsavedChanges(true);
            },
            container,
        });
    };

    // Conditions are activated/deactivated straight from the conditions list, so the change is
    // applied to the page rather than to the condition currently open in the editor.
    const toggleConditionIsActive = (index: number) => {
        const currentPageConditions = isNil(pageConditions) ? [] : pageConditions;
        const conditionToToggle = currentPageConditions[index];

        if (isNil(conditionToToggle)) {
            return;
        }

        const path = lensPath([index]);
        const toggledCondition = {
            ...conditionToToggle,
            // Conditions saved before this flag existed have no value, which is treated as active.
            isActive: conditionToToggle.isActive === false,
        };

        setPageConditions(set(path, toggledCondition, currentPageConditions) as PageCondition[]);

        // This is a page metadata change, so the page has to be saved now.
        toggleUnsavedChanges(true);
    };

    const applyRule = (rule: PageRule) => {
        const { condition, ruleIndex } = state;

        if (ruleIndex !== null && ruleIndex >= 0) {
            // update the [ruleIndex]th rule
            const path = lensPath([ruleIndex]);
            updateRules(set(path, rule, condition?.pageRules) as PageRule[]);
        } else {
            // add a new rule
            updateRules([...(condition?.pageRules as PageRule[]), rule]);
        }
        setModifiedPageConditions(true);
    };

    const applyOperation = (operation: PageOperation) => {
        const { condition, operationIndex } = state;

        if (operationIndex !== null && operationIndex >= 0) {
            // update the [operationIndex]th operation
            const path = lensPath([operationIndex]);
            updateOperations(set(path, operation, condition?.pageOperations) as PageOperation[]);
        } else {
            // add a new operation
            updateOperations([...(condition?.pageOperations as PageOperation[]), operation]);
        }
        setModifiedPageConditions(true);
    };

    const applyCondition = () => {
        const { condition, conditionIndex } = state;
        // Page conditions can start out as null so make sure we got at least an empty array in that case.
        const currentPageConditions = isNil(pageConditions) ? [] : pageConditions;

        if (conditionIndex !== null && conditionIndex >= 0) {
            // update the [conditionIndex]th operation
            const path = lensPath([conditionIndex]);
            setPageConditions(set(path, condition, currentPageConditions) as PageCondition[]);
        } else {
            // add a new condition
            setPageConditions([...currentPageConditions, condition] as PageCondition[]);
        }

        // Once the new/updated condition is applied, this becomes a page metadata change,
        // so the page has to be saved now.
        setModifiedPageConditions(false);
        toggleUnsavedChanges(true);
    };

    const contextValue = {
        state,
        updateCondition,
        updateConditionIndex,
        updateRuleIndex,
        updateOperationIndex,
        updateComparison,
        updateRules,
        updateOperations,
        reorderConditions,
        reorderRules,
        reorderOperations,
        deleteRule,
        deleteOperation,
        deleteCondition,
        toggleConditionIsActive,
        applyRule,
        applyOperation,
        applyCondition,
        updateValues,
        updateRefreshValues,
    };

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

const usePageCondition = () => {
    const context = useContext(Context);
    if (context === undefined) {
        throw new Error('No page conditions context!');
    }
    return context;
};

export { PageConditionProvider, usePageCondition };
